import type React from 'react';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import { useHomepagePageData } from '#src/app/pages/homepage/pageContext';
import {
	HeroTitle,
	PetitionMarquee,
	Tagline,
	TopImpactStoriesDesktop,
	TopImpactStoriesMobile,
	TopImpactStoriesMobileProvider,
} from '#src/app/pages/homepage/shared/components';
import { useHeroFeatureFlags } from '#src/app/pages/homepage/shared/hooks';

import { SocialPostUrlForm } from './components/SocialPostUrlForm';

type Props = Readonly<{
	/**
	 * Render the marquee's curated entries as the simple pre-card-types card. Set for the
	 * social_post arm of homepage_hero_experiment, which tests that card head-to-head against
	 * the curated shapes served to social_post_new. The prop comes out with the arm when
	 * testing wraps.
	 */
	simple?: boolean;
}>;

export const HeroSocialPostToDraft: React.FC<Props> = ({ simple = false }) => {
	const {
		data: { topImpactStories, shouldShowPetitionMarquee },
	} = useHomepagePageData();
	const {
		data: { flags },
	} = useHeroFeatureFlags();

	return (
		<section data-testid="homepage-social-post-hero-section" className="text-typography-strong relative">
			<div className="relative z-10 pt-6 md:pt-11 lg:pt-15">
				<div className="container">
					<HeroTitle className="m-0 text-center" />
					{/* tags every event in the link flow with the variant, including the shared captcha events */}
					<TrackingContext properties={{ multi_source: flags.multiInputSources ?? false }}>
						<SocialPostUrlForm />
					</TrackingContext>
					<Tagline />
				</div>

				{/* Show petition marquee in hero or fallback to top impact stories */}
				{shouldShowPetitionMarquee ? (
					<div className="m-auto max-w-480">
						<PetitionMarquee className="pt-5 md:pt-8" simple={simple} />
					</div>
				) : (
					<>
						{topImpactStories.length > 0 && (
							<div className="container">
								<TopImpactStoriesDesktop layout="row" />
							</div>
						)}
						{topImpactStories.length > 0 && (
							<TopImpactStoriesMobileProvider>
								<TopImpactStoriesMobile />
							</TopImpactStoriesMobileProvider>
						)}
					</>
				)}
			</div>
		</section>
	);
};
